import React, { useState, useEffect, useCallback } from "react";
import {
  Table,
  Spin,
  Card,
  Input,
  Button,
  Space,
  Modal,
  message,
  DatePicker,
} from "antd";
import { SearchOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import httpInjectorService from "../../services/http-injector.service";
import { useRefresh } from "../../RefreshContext";
import { Col, Row } from "reactstrap";

const { RangePicker } = DatePicker;

const Auditlogs = () => {
  const [auditData, setAuditData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [modalData, setModalData] = useState({ new_data: {}, old_data: {} });
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const { refreshKey } = useRefresh();
  const [pageSize, setPageSize] = useState(10);
  const isMobile = window.innerWidth < 768;

  const getAuditLogs = useCallback(async () => {
    console.log("getAuditLogs called with:", { startDate, endDate });
    setLoading(true);
    await new Promise((resolve) => setTimeout(resolve, 500));
    try {
      const payload = {
        ...(startDate && { start_date: startDate }),
        ...(endDate && { end_date: endDate }),
      };

      const response = await httpInjectorService.auditLogs(payload);

      if (response.status === "SUCCESS") {
        let data = response.data.map((log, idx) => {
          let candidateName = "";
          let email = "";
          let parsedLogs = {};

          try {
            parsedLogs = JSON.parse(log.logs || "{}");
            candidateName = parsedLogs.old_data?.[0]?.user_name || "";
            email = parsedLogs.old_data?.[0]?.email || "";
          } catch (e) {
            console.error("Error parsing logs:", e);
          }

          return {
            key: log.id || idx,
            candidateName,
            email,
            updatedBy: log.Name || "",
            updatedAt: log.updated_at, // only date
            updated_time: log.updated_time, // show time separately
            rawLog: parsedLogs,
          };
        });

        // data.sort((a, b) => new Date(b.updated_time) - new Date(a.updated_time));
        data = data.map((item, idx) => ({ ...item, sno: idx + 1 }));

        setAuditData(data);
        setFilteredData(data);
      } else {
        setAuditData([]);
        setFilteredData([]);
        message.error("Failed to load audit logs");
      }
    } catch (err) {
      console.error("Error:", err);
      message.error("Server error while loading audit logs");
    } finally {
      setLoading(false);
    }
  }, [startDate, endDate]);

  useEffect(() => {
    getAuditLogs();
  }, [refreshKey]);

  const handleSearch = (value) => {
    setSearchTerm(value);
    const lowerVal = value.toLowerCase();
    const filtered = auditData.filter((log) =>
      ["candidateName", "email", "updatedBy", "updatedAt", "updated_time"].some(
        (key) => (log[key] || "").toString().toLowerCase().includes(lowerVal)
      )
    );
    setFilteredData(filtered);
  };

  const columns = [
    {
      title: "S.No",
      dataIndex: "sno",
      key: "sno",
      sorter: (a, b) => a.sno - b.sno,
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
      width: 80,
    },
    {
      title: "Candidate Name",
      dataIndex: "candidateName",
      key: "candidateName",
      sorter: (a, b) => a.candidateName.localeCompare(b.candidateName),
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
      ellipsis: true,
    },
    !isMobile && {
      title: "Email",
      dataIndex: "email",
      key: "email",
      sorter: (a, b) => a.email.localeCompare(b.email),
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
      ellipsis: true,
    },
    {
      title: "Updated By",
      dataIndex: "updatedBy",
      key: "updatedBy",
      sorter: (a, b) => a.updatedBy.localeCompare(b.updatedBy),
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
      ellipsis: true,
    },
    {
      title: "Updated At",
      dataIndex: "updatedAt",
      key: "updatedAt",
      render: (text) => dayjs(text).format("DD-MM-YYYY"),
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
      sorter: (a, b) => new Date(a.updatedAt) - new Date(b.updatedAt),
      ellipsis: true,
    },

    {
      title: "Updated time",
      dataIndex: "updated_time",
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
      key: "updated_time",
      sorter: (a, b) => a.updated_time.localeCompare(b.updated_time),
      ellipsis: true,
    },
    {
      title: "Action",
      key: "action",
      render: (_, record) => (
        <Button
          type="link"
          onClick={() => {
            const newLogData = { ...record.rawLog };
            delete newLogData.old_data;
            setModalData({
              new_data: newLogData,
              old_data: record.rawLog.old_data?.[0] || {},
            });
            setIsModalVisible(true);
          }}
        >
          View
        </Button>
      ),
      onHeaderCell: () => ({
        style: { backgroundColor: "#5167c1", color: "black" },
      }),
      width: 80,
    },
  ].filter(Boolean);

  const renderComparisonTable = (oldData, newData) => {
    const fields = Object.keys(newData).filter((key) => key !== "old_data");
    return (
      <table
        style={{ width: "100%", borderCollapse: "collapse" }}
        border="1"
        cellPadding="8"
      >
        <thead style={{ backgroundColor: "#fafafa" }}>
          <tr>
            <th>Field</th>
            <th>Old Records</th>
            <th>New Records</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field) => (
            <tr key={field}>
              <td style={{ fontWeight: "600", textTransform: "capitalize" }}>
                {field}
              </td>
              <td>
                {oldData[field] !== undefined && oldData[field] !== ""
                  ? oldData[field]
                  : "-"}
              </td>
              <td>{newData[field]}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <React.Fragment>
      <Card bordered className="hide-scrollbar">
        <Row className="g-2 flex-wrap align-items-center mb-3">
          <Col xs="auto">
            <select
              className="form-select"
              value={pageSize}
              onChange={(e) => setPageSize(Number(e.target.value))}
            >
              {[10, 20, 30, 40, 50].map((size) => (
                <option key={size} value={size}>
                  Show {size}
                </option>
              ))}
            </select>
          </Col>

          <Col xs="auto" style={{ minWidth: "200px" }}>
            <div className="position-relative">
              <i
                className="bx bx-search-alt"
                style={{
                  position: "absolute",
                  top: "50%",
                  left: "12px",
                  transform: "translateY(-50%)",
                  fontSize: "18px",
                  color: "#aaa",
                }}
              ></i>
              <input
                onChange={(e) => handleSearch(e.target.value)}
                id="search-bar-0"
                type="text"
                className="form-control ps-5"
                placeholder={`${filteredData.length} record${
                  filteredData.length !== 1 ? "s" : ""
                }...`}
                value={searchTerm || ""}
                style={{ height: "38px" }}
              />
            </div>
          </Col>

          <Col xs="auto" style={{ float: "right" }}>
            <DatePicker
              placeholder="Start Date"
              size="large"
              value={startDate ? dayjs(startDate) : null}
              onChange={(date) =>
                setStartDate(date ? date.format("YYYY-MM-DD") : null)
              }
            />
          </Col>

          <Col xs="auto">
            <DatePicker
              placeholder="End Date"
              size="large"
              value={endDate ? dayjs(endDate) : null}
              onChange={(date) =>
                setEndDate(date ? date.format("YYYY-MM-DD") : null)
              }
            />
          </Col>

          <Col xs="auto">
            <Button
              type="primary"
              size="middle"
              onClick={getAuditLogs}
              style={{ backgroundColor: "#5167c1", borderColor: "#5167c1" }}
            >
              Apply Filter
            </Button>
          </Col>
        </Row>

        <Spin spinning={loading}>
          <div
            style={{ flex: 1, overflowY: "auto" }}
            className="hide-scrollbar"
          >
            <Table
              columns={columns}
              dataSource={filteredData}
              pagination={filteredData.length > pageSize ? { pageSize } : false}
              rowKey="key"
              scroll={{ x: "max-content" }}
            />
          </div>
        </Spin>
      </Card>

      <Modal
        title="Audit Log Details"
        visible={isModalVisible}
        onCancel={() => setIsModalVisible(false)}
        footer={null}
        width={800}
      >
        {renderComparisonTable(modalData.old_data, modalData.new_data)}
      </Modal>
    </React.Fragment>
  );
};

export default Auditlogs;
